/**
 * Client-side account helper. Talks to the realtime server's /api/account/*
 * endpoints (same origin, proxied by nginx). The token is kept in localStorage;
 * it identifies the account on both HTTP calls and the WebSocket hello.
 */

const TOKEN_KEY = "xq-token";

export interface Profile {
  username: string;
  displayName: string;
  rating: number;
  wins: number;
  losses: number;
  draws: number;
  games: number;
  lessonsDone: string[];
  emailNotifications?: boolean;
  memberSince?: number;
  archived?: number;
  /** Own profile only: the server does not put these on anyone else's. */
  email?: string;
  emailVerified?: boolean;
  dailyReminders?: boolean;
  tz?: string | null;
  /** the language the daily email is written in */
  lang?: "en" | "zh" | null;
  /** "declined" or "submitted" once they have answered the feedback prompt. */
  feedbackPrompt?: "declined" | "submitted" | null;
}

export interface GameSummary {
  id: string;
  endedAt: number;
  redName: string;
  blackName: string;
  redUser: string | null;
  blackUser: string | null;
  result: "red-wins" | "black-wins" | "draw";
  reason: string;
  rated: boolean;
  /** time-control pool, e.g. "5+0" (absent on games archived before pools existed) */
  tc?: string;
  ratingDelta: { red: number; black: number } | null;
}

export interface GameRecord extends GameSummary {
  redRating: number;
  blackRating: number;
  moves: { from: number; to: number }[];
}

let profile: Profile | null = null;

export function getToken(): string | null {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}
function setToken(tok: string) {
  try { localStorage.setItem(TOKEN_KEY, tok); localStorage.setItem(KNOWN_KEY, "1"); } catch { /* ignore */ }
}

/**
 * Has anyone ever signed in on this device? Decides whether a gate opens on
 * "Log in" or "Sign up". Every gate used to open on "Log in", so first-time
 * visitors typed a name they had never registered into a "Welcome back" form
 * and were told it was wrong — the bulk of failed sign-ins in October 2026.
 * Kept after signing out: someone who has had an account here is a returner.
 */
const KNOWN_KEY = "xq-known";
export function knownOnThisDevice(): boolean {
  try { return !!localStorage.getItem(KNOWN_KEY) || !!localStorage.getItem(TOKEN_KEY); } catch { return false; }
}

/** A refusal from the account API, with the server's code for translating it. */
export class ApiError extends Error {
  constructor(message: string, readonly code: string) { super(message); }
}
export function clearAccount() {
  try { localStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
  profile = null;
}
export function currentProfile(): Profile | null {
  return profile;
}
export function isLoggedIn(): boolean {
  return !!getToken();
}

async function post(path: string, body: unknown): Promise<any> {
  const res = await fetch(`/api/account/${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeader() },
    body: JSON.stringify(body),
  });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(j.error || `HTTP ${res.status}`, typeof j.code === "string" ? j.code : "");
  return j;
}
function authHeader(): Record<string, string> {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export async function signup(username: string, password: string, displayName: string, email: string, lessonsDone: string[]): Promise<Profile> {
  const j = await post("signup", { username, password, displayName, email, lessonsDone });
  setToken(j.token);
  profile = j.profile;
  return j.profile;
}

export async function login(username: string, password: string, lessonsDone: string[]): Promise<Profile> {
  const j = await post("login", { username, password, lessonsDone });
  setToken(j.token);
  profile = j.profile;
  return j.profile;
}

// ---- Continue with Google ----
/** Where the browser is sent to begin the flow. A real navigation, not fetch. */
export const GOOGLE_START = "/api/account/google/start";

interface Health { google?: boolean; discord?: string | null }
let healthAsk: Promise<Health> | null = null;
/**
 * What this deployment actually has configured.
 *
 * Asked once, lazily, and shared by everything that needs it — a deployment
 * without Google credentials or a Discord invite must not show a control that
 * cannot work, but neither should every page load pay for the question, and it
 * should certainly not be asked twice.
 */
function health(): Promise<Health> {
  healthAsk ??= fetch("/api/health").then((r) => r.json()).catch(() => ({} as Health));
  return healthAsk;
}

/** Is Google sign-in configured on this server? */
export function googleEnabled(): Promise<boolean> {
  return health().then((j) => !!j.google);
}

/** The public Discord invite, or "" when this deployment has none. */
export function discordInvite(): Promise<string> {
  return health().then((j) => (typeof j.discord === "string" ? j.discord : ""));
}

/**
 * Trade the one-time code Google's callback left in the URL fragment for the
 * session token. Single use: reloading the page with a spent code fails, which
 * is the point — the token itself is never in the URL.
 */
export async function googleExchange(code: string, lessonsDone: string[]): Promise<Profile> {
  const j = await post("google/exchange", { code, lessonsDone });
  setToken(j.token);
  profile = j.profile;
  return j.profile;
}

// ---- forgotten password (emailed one-time code) ----
/** Ask for a code. Resolves when the mail has been handed to the mail server. */
export async function requestResetCode(email: string): Promise<void> {
  await post("forgot", { email });
}

/** Check the code; the returned ticket is what authorises the change. */
export async function verifyResetCode(email: string, code: string): Promise<string> {
  const j = await post("verify-code", { email, code });
  return j.ticket as string;
}

/** Set the new password. The flow ends signed in, so this stores the token. */
export async function resetPassword(ticket: string, password: string): Promise<Profile> {
  const j = await post("reset", { ticket, password });
  setToken(j.token);
  profile = j.profile;
  return j.profile;
}

// ---- confirming the address a new account signed up with ----
/**
 * Ask for (or re-ask for) the confirmation code.
 *
 * Which account it goes to is decided by the server from the bearer token, so
 * there is nothing to pass: a caller cannot aim a code at somebody else's
 * mailbox. Resolves true when the address was already confirmed, in which case
 * there is nothing to type and the caller can just carry on.
 */
export async function sendVerification(): Promise<boolean> {
  const j = await post("send-verification", {});
  return !!j.verified;
}

/** Hand back the code from the inbox. Resolves to the refreshed own-profile. */
export async function confirmEmail(code: string): Promise<Profile> {
  const j = await post("confirm-email", { code });
  profile = j.profile;
  return j.profile;
}

/** Does this account still owe us a confirmed address? */
export function needsEmailVerification(): boolean {
  return !!profile && profile.emailVerified === false;
}

export async function logout(): Promise<void> {
  try { await post("logout", {}); } catch { /* ignore */ }
  clearAccount();
}

/** Load the profile for an existing token on boot; clears a stale token. */
export async function refreshProfile(): Promise<Profile | null> {
  if (!getToken()) return null;
  try {
    const res = await fetch("/api/account/me", { headers: authHeader() });
    if (res.status === 401) { clearAccount(); return null; }
    const j = await res.json();
    profile = j.profile;
    void reportTimezone();
    return profile;
  } catch {
    return null; // offline — keep the token, try again later
  }
}

/**
 * Tell the server which timezone this browser is in, so the daily reminder
 * arrives in the player's evening rather than at a fixed UTC hour. Sent only
 * when it differs from what the server already has.
 */
export async function reportTimezone(): Promise<void> {
  let tz = "";
  try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone ?? ""; } catch { /* zone unknown */ }
  // The language the app is read in, so the daily email arrives in it too.
  let lang = "";
  try { lang = localStorage.getItem("xq-lang") === "zh" ? "zh" : localStorage.getItem("xq-lang") === "en" ? "en" : ""; } catch { /* ignore */ }
  if (!profile) return;
  const body: Record<string, string> = {};
  if (tz && profile.tz !== tz) body.tz = tz;
  if (lang && profile.lang !== lang) body.lang = lang;
  if (!Object.keys(body).length) return;
  try {
    await fetch("/api/account/prefs", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeader() },
      body: JSON.stringify(body),
    });
    if (profile) { if (body.tz) profile.tz = body.tz; if (body.lang) profile.lang = body.lang as "en" | "zh"; }
  } catch { /* next boot tries again */ }
}

/**
 * Re-read our own profile from the server and hand back the rating.
 * The cached profile is written at login and never again, so after a rated game
 * the header and profile page kept showing the rating you signed in with.
 */
export async function refreshRating(): Promise<number | null> {
  const p = await refreshProfile();
  return p ? p.rating : null;
}

/** A player's public profile (own if username omitted and signed in). */
export async function fetchProfile(username?: string): Promise<Profile | null> {
  try {
    const res = username
      ? await fetch(`/api/profile/${encodeURIComponent(username)}`)
      : await fetch("/api/account/me", { headers: authHeader() });
    if (!res.ok) return null;
    const j = await res.json();
    return j.profile ?? null;
  } catch {
    return null;
  }
}

/** A player's finished-game history (metadata only). */
export async function fetchGames(username?: string, limit = 25): Promise<GameSummary[]> {
  try {
    const q = new URLSearchParams();
    if (username) q.set("user", username);
    q.set("limit", String(limit));
    const res = await fetch(`/api/games?${q.toString()}`, { headers: authHeader() });
    if (!res.ok) return [];
    const j = await res.json();
    return Array.isArray(j.games) ? j.games : [];
  } catch {
    return [];
  }
}

/** A single finished game with its full move list, for replay. */
export async function fetchGame(id: string): Promise<GameRecord | null> {
  try {
    const res = await fetch(`/api/game/${encodeURIComponent(id)}`);
    if (!res.ok) return null;
    const j = await res.json();
    return j.game ?? null;
  } catch {
    return null;
  }
}

// ---- notifications ----
export type NotifyKind = "friend_request" | "friend_accepted" | "challenge" | "message";
export interface Notification {
  id: string; kind: NotifyKind; fromUser: string; fromName: string;
  text?: string; at: number; read: boolean;
}
export interface Inbox { notifications: Notification[]; unread: number; unreadMessages: number }

export async function fetchNotifications(): Promise<Inbox> {
  const empty: Inbox = { notifications: [], unread: 0, unreadMessages: 0 };
  if (!getToken()) return empty;
  try {
    const res = await fetch("/api/notifications", { headers: authHeader() });
    if (!res.ok) return empty;
    const j = await res.json();
    return {
      notifications: j.notifications ?? [],
      unread: j.unread ?? 0,
      unreadMessages: j.unreadMessages ?? 0,
    };
  } catch {
    return empty;
  }
}

/** Mark some (or, with no ids, all) notifications read. Returns the new count. */
export async function markNotificationsRead(ids?: string[]): Promise<number> {
  if (!getToken()) return 0;
  try {
    const res = await fetch("/api/notifications/read", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeader() },
      body: JSON.stringify(ids ? { ids } : {}),
    });
    const j = await res.json().catch(() => ({}));
    return j.unread ?? 0;
  } catch {
    return 0;
  }
}

// ---- direct messages ----
export interface DirectMessage { id: string; from: string; to: string; text: string; at: number; read: boolean }
export interface Conversation {
  user: string; displayName: string; lastText: string; lastAt: number; unread: number; outgoing: boolean;
}

export async function fetchConversations(): Promise<{ conversations: Conversation[]; unread: number }> {
  const empty = { conversations: [], unread: 0 };
  if (!getToken()) return empty;
  try {
    const res = await fetch("/api/messages", { headers: authHeader() });
    if (!res.ok) return empty;
    const j = await res.json();
    return { conversations: j.conversations ?? [], unread: j.unread ?? 0 };
  } catch {
    return empty;
  }
}

export async function fetchThread(user: string): Promise<{ messages: DirectMessage[]; with: { username: string; displayName: string } | null }> {
  try {
    const res = await fetch(`/api/messages/${encodeURIComponent(user)}`, { headers: authHeader() });
    if (!res.ok) return { messages: [], with: null };
    const j = await res.json();
    return { messages: j.messages ?? [], with: j.with ?? null };
  } catch {
    return { messages: [], with: null };
  }
}

export async function postMessage(to: string, text: string): Promise<{ ok: boolean; error?: string; message?: DirectMessage }> {
  try {
    const res = await fetch("/api/messages", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeader() },
      body: JSON.stringify({ to, text }),
    });
    const j = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, error: j.error || `HTTP ${res.status}` };
    return { ok: true, message: j.message };
  } catch {
    return { ok: false, error: "Network error." };
  }
}

/** Turn notification email on or off for this account. */
export async function setEmailNotifications(on: boolean): Promise<boolean> {
  try {
    const res = await fetch("/api/account/prefs", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeader() },
      body: JSON.stringify({ emailNotifications: on }),
    });
    const j = await res.json().catch(() => ({}));
    if (profile) profile.emailNotifications = j.emailNotifications ?? on;
    return j.emailNotifications ?? on;
  } catch {
    return on;
  }
}

/**
 * Record, on the ACCOUNT, that this person has answered the feedback prompt.
 *
 * "No, thanks" has to mean never again, and a promise kept only in this
 * browser's localStorage is broken by their next device. Fire and forget: the
 * local answer is already stored, and a failed request must not make the
 * dialog reappear.
 */
export async function setFeedbackPrompt(state: "declined" | "submitted"): Promise<void> {
  if (!getToken()) return;
  try {
    await fetch("/api/account/prefs", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeader() },
      body: JSON.stringify({ feedbackPrompt: state }),
    });
    if (profile) profile.feedbackPrompt = state;
  } catch {
    /* the local answer stands regardless */
  }
}

/** Turn the daily "you haven't played today" reminder on or off. */
export async function setDailyReminders(on: boolean): Promise<boolean> {
  try {
    const res = await fetch("/api/account/prefs", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeader() },
      body: JSON.stringify({ dailyReminders: on }),
    });
    const j = await res.json().catch(() => ({}));
    if (profile) profile.dailyReminders = j.dailyReminders ?? on;
    return j.dailyReminders ?? on;
  } catch {
    return on;
  }
}

// ---- friends ----
export interface Friend { username: string; displayName: string; rating: number; online: boolean; inGame: boolean; }
export interface FriendReq { username: string; displayName: string; }
export interface FriendGraph { friends: Friend[]; incoming: FriendReq[]; outgoing: FriendReq[]; }

export async function fetchFriends(): Promise<FriendGraph> {
  const empty: FriendGraph = { friends: [], incoming: [], outgoing: [] };
  if (!getToken()) return empty;
  try {
    const res = await fetch("/api/friends", { headers: authHeader() });
    if (!res.ok) return empty;
    const j = await res.json();
    return { friends: j.friends ?? [], incoming: j.incoming ?? [], outgoing: j.outgoing ?? [] };
  } catch {
    return empty;
  }
}

async function friendAction(path: string, body: unknown): Promise<{ ok: boolean; error?: string }> {
  try {
    const res = await fetch(`/api/friends/${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeader() },
      body: JSON.stringify(body),
    });
    const j = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, error: j.error || `HTTP ${res.status}` };
    return { ok: true };
  } catch {
    return { ok: false, error: "Network error." };
  }
}
export const requestFriend = (to: string) => friendAction("request", { to });
export const acceptFriend = (from: string) => friendAction("accept", { from });
export const declineFriend = (from: string) => friendAction("decline", { from });
export const unfriend = (user: string) => friendAction("remove", { user });

export async function markLessonDone(id: string): Promise<void> {
  if (!getToken()) return;
  try {
    const res = await fetch("/api/account/lessons", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeader() },
      body: JSON.stringify({ id }),
    });
    const j = await res.json().catch(() => ({}));
    if (j.lessonsDone && profile) profile.lessonsDone = j.lessonsDone;
  } catch { /* ignore; local progress still saved */ }
}
